<template>
  <button
    v-if="sessionStore.user?.id"
    type="button"
    class="p-1.5 transition-all"
    :class="buttonClass"
    :disabled="likeLoading"
    @click.stop.prevent="toggleLike"
  >
    <component
      :is="HeartIconSolid"
      v-if="!likeLoading"
      class="transition-colors"
      :class="[iconClass, isLiked ? 'text-red-600' : 'text-gray-500 hover:text-gray-700']"
    />
    <CircularSpinner v-else />
  </button>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { HeartIcon as HeartIconSolid } from '@heroicons/vue/24/solid';
import { CircularSpinner } from '../../assets/images/icons';
import { useMutation } from 'vue-query';
import productLikesApi from 'api/product-likes';
import useSessionStore from 'stores/user-store';

const props = withDefaults(
  defineProps<{
    productId: number;
    initialIsLiked?: boolean;
    buttonClass?: string;
    iconClass?: string;
  }>(),
  {
    initialIsLiked: false,
    buttonClass: '',
    iconClass: 'size-5',
  },
);

const sessionStore = useSessionStore();
const isLiked = ref(props.initialIsLiked);

const createLikeMutation = useMutation(() => productLikesApi.create(props.productId), {
  onSuccess: () => {
    isLiked.value = true;
  },
  onError: () => {
    isLiked.value = false;
  },
});

const deleteLikeMutation = useMutation(() => productLikesApi.delete(props.productId), {
  onSuccess: () => {
    isLiked.value = false;
  },
  onError: () => {
    isLiked.value = true;
  },
});

const likeLoading = computed(() => createLikeMutation.isLoading.value || deleteLikeMutation.isLoading.value);

function toggleLike(event?: Event) {
  if (event) {
    event.stopPropagation();
    event.preventDefault();
  }

  if (likeLoading.value) return;

  if (!isLiked.value) {
    createLikeMutation.mutate();
  } else {
    deleteLikeMutation.mutate();
  }
}
</script>
